import { Steps } from '@ark-ui/react/steps';
import type React from 'react';
import { colors } from '../theme/colors';

export interface StepItem {
  value: string;
  title: string;
}

interface StepsProps {
  items: StepItem[];
  currentStep?: number;
  onStepChange?: (step: number) => void;
  orientation?: 'horizontal' | 'vertical';
  showNavigation?: boolean;
  showCompletedContent?: boolean;
  completedContent?: React.ReactNode;
  renderStepContent?: (step: StepItem, index: number) => React.ReactNode;
  scrollTargets?: Record<number, string>;
}

export const StepsComponent: React.FC<StepsProps> = ({
  items,
  currentStep = 0,
  onStepChange,
  orientation = 'horizontal',
  showNavigation = true,
  showCompletedContent = true,
  completedContent,
  renderStepContent,
  scrollTargets,
}) => {
  const handleStepChange = (details: any) => {
    const stepIndex =
      details.index !== undefined
        ? details.index
        : items.findIndex((item) => item.value === details.value);
    
    if (stepIndex !== -1) {
      if (onStepChange) {
        onStepChange(stepIndex);
      }

      scrollToStep(stepIndex);
    }
  };

  const scrollToStep = (stepIndex: number) => {
    if (scrollTargets && scrollTargets[stepIndex]) {
      const targetSelector = scrollTargets[stepIndex];
      const target = document.getElementById(targetSelector) || 
                    document.querySelector(targetSelector);
      if (target) {
        setTimeout(() => {
          target.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }, 0);
      }
    }
  };

  const handleStepClick = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    scrollToStep(index);
    
    if (index <= currentStep && onStepChange) {
      onStepChange(index);
    }
  };

  const getStepStatus = (index: number) => {
    if (index < currentStep) return 'complete';
    if (index === currentStep) return 'current';
    return 'incomplete';
  };

  const getStepStyle = (index: number) => {
    const status = getStepStatus(index);
    const isCurrent = status === 'current';
    const isComplete = status === 'complete';

    return {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      transition: 'all 0.2s ease',
      backgroundColor: isCurrent ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
      color: isComplete
        ? colors.accent.green
        : isCurrent
          ? colors.accent.blue
          : colors.neutral.grey[500],
    };
  };

  const getIndicatorStyle = (index: number) => {
    const status = getStepStatus(index);
    const isCurrent = status === 'current';
    const isComplete = status === 'complete';

    return {
      width: '2rem',
      height: '2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      fontSize: '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      backgroundColor: isComplete
        ? colors.accent.green
        : isCurrent
          ? colors.accent.blue
          : colors.neutral.grey[200],
      color: isComplete || isCurrent ? colors.neutral.white : colors.neutral.grey[600],
      border: isCurrent ? `2px solid ${colors.accent.blue}` : 'none',
    };
  };

  const getSeparatorStyle = (index: number) => {
    const isComplete = index < currentStep;
    return {
      flex: 1,
      height: '2px',
      backgroundColor: isComplete ? colors.accent.green : colors.neutral.grey[200],
      transition: 'all 0.2s ease',
      margin: orientation === 'horizontal' ? '0 0.5rem' : '0.5rem 0',
    };
  };

  return (
    <Steps.Root count={items.length} onStepChange={(details) => handleStepChange(details)}>
      <Steps.List
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4rem',
          margin: '0.5rem auto',
        }}
      >
        {items.map((item, index) => (
          <Steps.Item key={item.value} index={index}>
            <Steps.Trigger 
              style={getStepStyle(index)} 
              disabled={index > currentStep}
              onClick={(e) => handleStepClick(index, e)}
            >
              <Steps.Indicator style={getIndicatorStyle(index)}>
                {getStepStatus(index) === 'complete' ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3333 4L6 11.3333L2.66667 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  index + 1
                )}
              </Steps.Indicator>
              <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{item.title}</span>
            </Steps.Trigger>
            {index < items.length - 1 && <Steps.Separator style={getSeparatorStyle(index)} />}
          </Steps.Item>
        ))}
      </Steps.List>
    </Steps.Root>
  );
};
